import type maplibregl from 'maplibre-gl';
import { EventEmitter } from '../events/MapEvent';

export interface SourceOptions {
  attributions?: string;
}

interface Attachment {
  map: maplibregl.Map;
  sourceId: string;
}

export class Source extends EventEmitter {
  public attributions?: string;
  protected _attachments: Attachment[] = [];

  constructor(options: SourceOptions = {}) {
    super();
    this.attributions = options.attributions;
  }

  getAttributions(): string | undefined {
    return this.attributions;
  }

  refresh(): void {
    this.emit('change');
  }

  /**
   * Translator entry point: emit a MapLibre source spec for this source.
   * Returned as `unknown` so engine types stay out of the public surface;
   * `Layer._ensureSource` casts internally before passing to MapLibre.
   */
  toMaplibreSpec(): unknown {
    throw new Error(`${this.constructor.name}.toMaplibreSpec() not implemented`);
  }

  /**
   * Translator hook called by `Layer._ensureSource` once the source has been
   * registered on a live MapLibre map. `mapHandle` is opaque on the public
   * surface (engine type kept internal).
   */
  _attach(mapHandle: unknown, sourceId: string): void {
    const map = mapHandle as maplibregl.Map;
    this._attachments.push({ map, sourceId });
  }

  _detach(mapHandle: unknown, sourceId: string): void {
    const map = mapHandle as maplibregl.Map;
    this._attachments = this._attachments.filter(
      (a) => !(a.map === map && a.sourceId === sourceId)
    );
  }

  protected _forEachAttachment(fn: (a: Attachment) => void): void {
    for (const a of this._attachments) {
      try { fn(a); } catch {}
    }
  }
}

export interface OsmSourceOptions {
  url?: string;
  crossOrigin?: string;
  maxZoom?: number;
}

export class OsmSource extends Source {
  public url: string;
  public crossOrigin: string;
  public maxZoom: number;

  constructor(options: OsmSourceOptions = {}) {
    super({ attributions: '\u00a9 OpenStreetMap contributors' });
    this.url = options.url || 'https://tile.openstreetmap.org/{z}/{x}/{y}.png';
    this.crossOrigin = options.crossOrigin || 'anonymous';
    this.maxZoom = options.maxZoom || 19;
  }

  getTileUrl(z: number, x: number, y: number): string {
    return this.url.replace('{z}', String(z)).replace('{x}', String(x)).replace('{y}', String(y));
  }

  toMaplibreSpec(): unknown {
    return {
      type: 'raster',
      tiles: [this.url],
      tileSize: 256,
      maxzoom: this.maxZoom,
      attribution: this.attributions,
    };
  }
}

export interface XYZSourceOptions {
  url: string;
  minZoom?: number;
  maxZoom?: number;
  tileSize?: number;
  crossOrigin?: string;
  projection?: string;
  tilePixelRatio?: number;
  attributions?: string;
  headers?: Record<string, string>;
}

export class XYZSource extends Source {
  public url: string;
  public minZoom: number;
  public maxZoom: number;
  public tileSize: number;
  public crossOrigin: string;
  public projection: string;

  constructor(options: XYZSourceOptions) {
    super({ attributions: options.attributions });
    this.url = options.url;
    this.minZoom = options.minZoom || 0;
    this.maxZoom = options.maxZoom || 22;
    this.tileSize = options.tileSize || 256;
    this.crossOrigin = options.crossOrigin || 'anonymous';
    this.projection = options.projection || 'EPSG:3857';
  }

  getTileUrl(z: number, x: number, y: number): string {
    return this.url
      .replace('{z}', String(z))
      .replace('{x}', String(x))
      .replace('{y}', String(y));
  }

  toMaplibreSpec(): unknown {
    return {
      type: 'raster',
      tiles: [this.url],
      tileSize: this.tileSize,
      minzoom: this.minZoom,
      maxzoom: this.maxZoom,
      attribution: this.attributions,
    };
  }
}

export interface GeoJsonSourceOptions {
  url?: string;
  data?: GeoJSON.GeoJsonObject;
  projection?: string;
  useSpatialIndex?: boolean;
  strategy?: 'all' | 'bbox';
  attributions?: string;
  cluster?: boolean;
  clusterRadius?: number;
  clusterMaxZoom?: number;
}

export class GeoJsonSource extends Source {
  public url?: string;
  public data?: GeoJSON.GeoJsonObject;
  public projection: string;
  public cluster: boolean;
  public clusterRadius: number;
  public clusterMaxZoom?: number;

  constructor(options: GeoJsonSourceOptions = {}) {
    super({ attributions: options.attributions });
    this.url = options.url;
    this.data = options.data;
    this.projection = options.projection || 'EPSG:4326';
    this.cluster = options.cluster || false;
    this.clusterRadius = options.clusterRadius ?? 50;
    this.clusterMaxZoom = options.clusterMaxZoom;
  }

  setData(data: GeoJSON.GeoJsonObject): void {
    this.data = data;
    this._forEachAttachment(({ map, sourceId }) => {
      const src = map.getSource(sourceId) as maplibregl.GeoJSONSource | undefined;
      if (src && typeof src.setData === 'function') {
        src.setData(data as any);
      }
    });
    this.emit('change');
  }

  toMaplibreSpec(): unknown {
    const spec: any = {
      type: 'geojson',
      data: this.data ?? this.url ?? { type: 'FeatureCollection', features: [] },
    };
    if (this.cluster) {
      spec.cluster = true;
      spec.clusterRadius = this.clusterRadius;
      if (this.clusterMaxZoom !== undefined) spec.clusterMaxZoom = this.clusterMaxZoom;
    }
    return spec;
  }
}

export interface WMSSourceOptions {
  url: string;
  layers: string[];
  format?: string;
  transparent?: boolean;
  version?: string;
  crs?: string;
  styles?: string[];
  tiled?: boolean;
  tileSize?: number;
  params?: Record<string, string>;
  headers?: Record<string, string>;
  attributions?: string;
}

export class WMSSource extends Source {
  public url: string;
  public layers: string[];
  public format: string;
  public transparent: boolean;
  public version: string;
  public crs: string;
  public tiled: boolean;
  public tileSize: number;
  public params: Record<string, string>;

  constructor(options: WMSSourceOptions) {
    super({ attributions: options.attributions });
    this.url = options.url;
    this.layers = options.layers;
    this.format = options.format || 'image/png';
    this.transparent = options.transparent !== false;
    this.version = options.version || '1.3.0';
    this.crs = options.crs || 'EPSG:3857';
    this.tiled = options.tiled !== false;
    this.tileSize = options.tileSize || 256;
    this.params = options.params || {};
  }

  updateParams(params: Record<string, string>): void {
    this.params = { ...this.params, ...params };
    this.emit('change');
  }

  private _buildTileUrl(): string {
    const sep = this.url.includes('?') ? '&' : '?';
    const isV13 = this.version.startsWith('1.3');
    const bboxParam = isV13 ? '{bbox-epsg-3857}' : '{bbox-epsg-3857}';
    const crsKey = isV13 ? 'CRS' : 'SRS';
    const params: Record<string, string> = {
      SERVICE: 'WMS',
      VERSION: this.version,
      REQUEST: 'GetMap',
      LAYERS: this.layers.join(','),
      FORMAT: this.format,
      TRANSPARENT: String(this.transparent),
      [crsKey]: this.crs,
      WIDTH: String(this.tileSize),
      HEIGHT: String(this.tileSize),
      BBOX: bboxParam,
      ...this.params,
    };
    const qs = Object.entries(params)
      .map(([k, v]) => `${k}=${v}`)
      .join('&');
    return `${this.url}${sep}${qs}`;
  }

  toMaplibreSpec(): unknown {
    return {
      type: 'raster',
      tiles: [this._buildTileUrl()],
      tileSize: this.tileSize,
      attribution: this.attributions,
    };
  }
}

export interface MVTSourceOptions {
  url: string;
  layers?: string[];
  minZoom?: number;
  maxZoom?: number;
  attributions?: string;
}

export class MVTSource extends Source {
  public url: string;
  public layers?: string[];
  public minZoom: number;
  public maxZoom: number;

  constructor(options: MVTSourceOptions) {
    super({ attributions: options.attributions });
    this.url = options.url;
    this.layers = options.layers;
    this.minZoom = options.minZoom || 0;
    this.maxZoom = options.maxZoom || 14;
  }

  toMaplibreSpec(): unknown {
    const isTileJson = this.url.endsWith('.json') || this.url.includes('TileJSON');
    if (isTileJson) {
      return { type: 'vector', url: this.url } as any;
    }
    return {
      type: 'vector',
      tiles: [this.url],
      minzoom: this.minZoom,
      maxzoom: this.maxZoom,
      attribution: this.attributions,
    };
  }
}

export interface DEMSourceOptions {
  url: string;
  format?: string;
  resolution?: number;
  encoding?: 'mapbox' | 'terrarium' | 'custom';
  tileSize?: number;
  attributions?: string;
}

export class DEMSource extends Source {
  public url: string;
  public format: string;
  public encoding: 'mapbox' | 'terrarium' | 'custom';
  public tileSize: number;

  constructor(options: DEMSourceOptions) {
    super({ attributions: options.attributions });
    this.url = options.url;
    this.format = options.format || 'png';
    this.encoding = options.encoding || 'mapbox';
    this.tileSize = options.tileSize || 256;
  }

  toMaplibreSpec(): unknown {
    return {
      type: 'raster-dem',
      tiles: [this.url],
      tileSize: this.tileSize,
      encoding: this.encoding === 'custom' ? 'mapbox' : this.encoding,
      attribution: this.attributions,
    } as any;
  }
}

export interface CSVSourceOptions {
  url?: string;
  data?: string;
  latField?: string;
  lonField?: string;
  delimiter?: string;
}

export class CSVSource extends Source {
  public url?: string;
  public data?: string;
  public latField: string;
  public lonField: string;
  public delimiter: string;
  private _geojson: GeoJSON.FeatureCollection;

  constructor(options: CSVSourceOptions) {
    super();
    this.url = options.url;
    this.data = options.data;
    this.latField = options.latField || 'lat';
    this.lonField = options.lonField || 'lon';
    this.delimiter = options.delimiter || ',';
    this._geojson = this.data ? this._parseCsv(this.data) : { type: 'FeatureCollection', features: [] };
  }

  private _parseCsv(text: string): GeoJSON.FeatureCollection {
    const lines = text.trim().split(/\r?\n/);
    if (lines.length < 2) return { type: 'FeatureCollection', features: [] };
    const headers = lines[0].split(this.delimiter).map((h) => h.trim());
    const latIdx = headers.indexOf(this.latField);
    const lonIdx = headers.indexOf(this.lonField);
    const features: GeoJSON.Feature[] = [];
    for (let i = 1; i < lines.length; i++) {
      const cols = lines[i].split(this.delimiter);
      const lat = parseFloat(cols[latIdx]);
      const lon = parseFloat(cols[lonIdx]);
      if (!isFinite(lat) || !isFinite(lon)) continue;
      const props: Record<string, string> = {};
      headers.forEach((h, j) => { if (j !== latIdx && j !== lonIdx) props[h] = cols[j]; });
      features.push({ type: 'Feature', geometry: { type: 'Point', coordinates: [lon, lat] }, properties: props });
    }
    return { type: 'FeatureCollection', features };
  }

  toMaplibreSpec(): unknown {
    return { type: 'geojson', data: this._geojson } as any;
  }
}

export interface StreamSourceOptions {
  wsUrl: string;
  protocol?: string;
  bufferSize?: number;
  reconnect?: boolean;
}

export class StreamSource extends Source {
  public wsUrl: string;
  public protocol?: string;
  public bufferSize: number;
  private ws?: WebSocket;
  private _features: GeoJSON.Feature[] = [];

  constructor(options: StreamSourceOptions) {
    super();
    this.wsUrl = options.wsUrl;
    this.protocol = options.protocol;
    this.bufferSize = options.bufferSize || 100;
  }

  connect(): void {
    this.ws = new WebSocket(this.wsUrl, this.protocol);
    this.ws.onmessage = (event) => {
      const data = JSON.parse(event.data);
      this.emit('message', { data });
      if (data.type === 'Feature') {
        this._features.push(data);
        if (this._features.length > this.bufferSize) this._features.shift();
        const fc: GeoJSON.FeatureCollection = { type: 'FeatureCollection', features: this._features };
        this._forEachAttachment(({ map, sourceId }) => {
          const src = map.getSource(sourceId) as maplibregl.GeoJSONSource | undefined;
          if (src && typeof src.setData === 'function') src.setData(fc as any);
        });
      }
    };
    this.ws.onopen = () => this.emit('open');
    this.ws.onclose = () => this.emit('close');
    this.ws.onerror = () => this.emit('error', { error: new Error('WebSocket error') });
  }

  disconnect(): void {
    this.ws?.close();
  }

  toMaplibreSpec(): unknown {
    return { type: 'geojson', data: { type: 'FeatureCollection', features: this._features } } as any;
  }
}
